import { defineStore } from 'pinia/dist/pinia.esm-browser';
import { reactive, ref } from 'vue';
import Message from '@/models/message';
import { ChatTopEnum, ChatType, loadChatType, RETURN_VISIT } from '@/enum/chat';
import dayjs from 'dayjs';
import useUserStore from '@/store/user';

import { getMsgTime } from '@/utils/time';
import { isAssistantRole } from '@/hooks/teacher';
import { useRoute } from 'vue-router';
import { useTabList } from '@/views/home/chat/components/hooks';

const useList = () => {
  const list = ref([]);
  let timer = null;

  const refreshTime = () => {
    timer = setTimeout(() => {
      console.log(`[${dayjs().format('YYYY-MM-DD HH:mm:ss')}] 更新對話列表__msg_time時間`);

      list.value.forEach((item) => {
        item.__msg_time = getMsgTime(item.updated_at);
      });

      refreshTime();
    }, 10 * 1000);
  };

  refreshTime();

  return list;
};

const useChatListStore = defineStore('chatList', () => {
  const list = useList();
  const loading = ref(false);
  const error = ref(null);
  const route = ref();
  const queryParams = reactive({
    nickname: '',
    type: ChatType.ALL,
  });

  // 是否還有更多數據
  const moreData = ref(true);

  const page = ref(1);
  const msgNum = reactive({
    all: 0,
    new: 0,
    unanswered: 0,
    key_customers: 0,
  });

  let abortController;

  // 用來做去重的map
  const msgMap = new Map();
  const pendingServiceInfo = new Map();

  const clearError = () => {
    error.value = null;
  };

  const setQueryParams = ({ nickname, type } = {}) => {
    if (typeof nickname === 'string') {
      queryParams.nickname = nickname;
    }

    if (typeof type === 'number') {
      queryParams.type = type;
    }
  };

  const deleteItemByRoomId = (room_id) => {
    const index = list.value.findIndex((item) => item.room_id === room_id);
    list.value.splice(index, 1);
  };

  const setMapItem = (room_id, update_time) => {
    const mapItem = msgMap.get(room_id, update_time);
    if (mapItem) {
      mapItem.update_time = update_time;
      return;
    }

    msgMap.set(room_id, {
      update_time,
    });
  };

  /**
   * 刪除時間更久遠的item
   * @param room_id
   * @param update_time
   * @returns {boolean} 是否刪除
   */
  const removeOlderItem = (room_id, update_time) => {
    const mapItem = msgMap.get(room_id);

    if (!mapItem) {
      setMapItem(room_id, update_time);
      return true;
    }

    if (mapItem.update_time > update_time) return false;

    deleteItemByRoomId(room_id);
    setMapItem(room_id, update_time);
    return true;
  };

  // 不會發生重複item的push方法
  const noRepeatPush = (item) => {
    const { room_id, updated_at } = item;

    if (removeOlderItem(room_id, updated_at)) {
      const roomKey = String(room_id);
      const serviceInfo = pendingServiceInfo.get(roomKey);
      if (serviceInfo) {
        assignServiceInfo(item, serviceInfo);
        pendingServiceInfo.delete(roomKey);
      }
      list.value.push(item);
    }
  };

  const clearList = () => {
    list.value = [];
    msgMap.clear();
  };

  const getMoreData = async ({ nickname, token, type, countName }) => {
    if (!moreData.value) return;
    if (loading.value) return;

    loading.value = true;
    try {
      abortController = new AbortController();
      const res = await Message.getFreeChatList({
        page: page.value,
        token,
        signal: abortController.signal,
        nickname,
        type,
      });

      if (!Array.isArray(res.list)) {
        loading.value = false;
        return;
      }

      const { total_page = 1, current_page = 1, total_count } = res.pager;

      if (current_page === 1) {
        list.value = [];
      }

      if (countName) {
        msgNum[countName] = total_count;
      }

      res.list.forEach((item) => {
        item.__msg_time = getMsgTime(item.updated_at);
        noRepeatPush(item);
      });

      if (page.value >= total_page) {
        moreData.value = false;
      }

      page.value = current_page + 1;
    } catch (e) {
      if (!e.__CANCEL__) {
        error.value = e;
      }
    } finally {
      loading.value = false;
    }
  };

  const getMsgNum = (() => {
    let loading = false;
    const abortControllers = [];

    const fn = async ({ token, route }) => {
      if (loading) return;
      loading = true;

      let teacherChatType;
      if (isAssistantRole.value) {
        const userStore = useUserStore();
        let res = await Message.getMsgCategory({
          token: userStore.getTeacherTokenByUid(route.params.teacher_uid),
        }).catch((e) => {
          return Promise.reject(e);
        });

        if (route.params.role === 'user') {
          res = res.user_category;
        } else {
          res = res.teacher_category;
        }
        teacherChatType = res.map((item) => item.id);
      }

      try {
        const types = isAssistantRole.value ? teacherChatType : Object.values(loadChatType);
        const res = await Promise.all(
          types.map((type) => {
            const abortController = new AbortController();
            abortControllers.push(abortController);

            return Message.getFreeChatList({
              page: 1,
              size: 1,
              token,
              signal: abortController.signal,
              type,
            });
          }),
        );

        // 當前老師不是助理角色
        if (!isAssistantRole.value) {
          const [allPager, newPager, unansweredPager, keyCustomersPager] = res.map(
            (item) => item.pager || {},
          );

          msgNum.all = allPager.total_count || 0;
          msgNum.new = newPager.total_count || 0;
          msgNum.unanswered = unansweredPager.total_count || 0;
          // msgNum.key_customers = keyCustomersPager.unread_num || 0;
        } else {
          teacherChatType.forEach((item, index) => {
            msgNum[item] = res[index].pager.total_count || 0;
          });
        }
      } finally {
        loading = false;
        clearAbortControls();
      }
    };

    fn.reset = () => {
      loading = false;
      abortControllers.forEach((abortController) => abortController?.abort?.());
      clearAbortControls();
    };

    const clearAbortControls = () => abortControllers.splice(0);

    return fn;
  })();

  let updateListMarkTime;
  const updateList = async (token) => {
    if (loading.value) return;
    if (page.value <= 1) return;

    const currentTime = dayjs().valueOf();
    updateListMarkTime = currentTime;
    const promiseList = [];
    for (let i = 1; i <= page.value - 1; i++) {
      const promise = Message.getFreeChatList({
        page: i,
        token,
      });
      promiseList.push(promise);
    }

    const res = await Promise.all(promiseList);

    if (updateListMarkTime !== currentTime) return;
    const newList = res.map((item) => item.list).flat(1);
    clearList();
    newList.forEach((item) => noRepeatPush(item));
  };

  const reset = () => {
    clearList();
    pendingServiceInfo.clear();
    updateListMarkTime = dayjs().valueOf();
    abortController && abortController.abort();
    loading.value = false;
    moreData.value = true;
    page.value = 1;
    error.value = null;
    abortController = null;
  };

  const resetMsgNum = () => {
    msgNum.all = 0;
    msgNum.new = 0;
    msgNum.unanswered = 0;
    getMsgNum.reset?.();
  };

  const refreshCurrentList = async ({ token, route: currentRoute, countName } = {}) => {
    if (!token) return;
    if (queryParams.type === RETURN_VISIT) return;

    reset();
    getMsgNum({
      token,
      route: currentRoute || route.value,
    });
    await getMoreData({
      token,
      nickname: queryParams.nickname,
      type: queryParams.type,
      countName,
    });
  };

  const getIndexByRoomId = (roomId) => {
    return list.value.findIndex((item) => String(item.room_id) === String(roomId));
  };

  const getChatItemByRoomId = (roomId) => {
    return list.value.find((item) => String(item.room_id) === String(roomId));
  };

  const setToTopStatus = (roomId, is_top) => {
    const roomInfo = getChatItemByRoomId(roomId);
    if (!roomInfo) return;
    roomInfo.is_top = !!is_top ? ChatTopEnum.TOP : ChatTopEnum.NOT_TOP;
  };

  const insertChatItem = (chatItem) => {
    // 下面的是排序了
    const topList = [];
    const notTopList = [];
    list.value.forEach((item) => {
      if (item.is_top) {
        topList.push(item);
        return;
      }
      notTopList.push(item);
    });

    if (chatItem.is_top) {
      topList.unshift(chatItem);
    } else {
      notTopList.unshift(chatItem);
    }

    list.value = topList.concat(notTopList);
  };

  const assignServiceInfo = (target, source = {}) => {
    const hasOwn = (key) => Object.prototype.hasOwnProperty.call(source, key);

    if (hasOwn('is_process_service')) {
      target.is_process_service = source.is_process_service;
    }

    if (hasOwn('last_service')) {
      target.last_service = source.last_service;
    }

    if (hasOwn('service_info')) {
      target.service_info = source.service_info;
    }

    if (hasOwn('service_status')) {
      target.service_status = source.service_status;
    }

    if (hasOwn('service_status_text')) {
      target.service_status_text = source.service_status_text;
    }

    if (hasOwn('service_status_color')) {
      target.service_status_color = source.service_status_color;
    }

    if (hasOwn('badge_text')) {
      target.badge_text = source.badge_text;
    }

    if (hasOwn('badge_color')) {
      target.badge_color = source.badge_color;
    }
  };

  const updateServiceInfo = (roomId, data) => {
    const roomKey = String(roomId);
    const serviceInfo = { ...pendingServiceInfo.get(roomKey) };
    if (Object.prototype.hasOwnProperty.call(data, 'last_service')) {
      const lastService = data.last_service;
      assignServiceInfo(serviceInfo, {
        last_service: lastService,
        service_info: null,
        service_status: lastService?.service_status ?? 0,
        service_status_text: lastService?.service_status_text ?? '',
        service_status_color: lastService?.service_status_color ?? 0,
        badge_text: '',
        badge_color: 0,
      });
    }
    assignServiceInfo(serviceInfo, data);

    const item = getChatItemByRoomId(roomId);
    if (item) assignServiceInfo(item, serviceInfo);

    // Keep pushes received before the list response so loading cannot discard them.
    if (!item || loading.value) {
      pendingServiceInfo.set(roomKey, serviceInfo);
    }
  };

  const updateChatItem = (msg_data) => {
    let data;
    // 如果是第一頁還在加載中，則不更新
    if (loading.value && page.value === 1) return;

    // 區分是跟用户聊天還是跟老師聊天
    const currentUrl = window.location.href;
    const role = currentUrl.includes('teacher') ? 2 : 1;
    if (role === 1) {
      if (![msg_data.from_user.type, msg_data.to_user.type].includes(1)) return;
      data = msg_data;
    }

    // 老師與老師聊天，重組消息數據
    if (role === 2) {
      if ([msg_data.from_user.type, msg_data.to_user.type].includes(1)) return;
      const is_me = currentUrl.includes(msg_data.from_user.uid);
      const current_user = is_me ? 'from_user' : 'to_user';
      const opposite_user = is_me ? 'to_user' : 'from_user';
      data = {
        ...msg_data,
        ...msg_data[opposite_user],
        user: msg_data[opposite_user],
        notice: msg_data[current_user].notice,
        is_top: msg_data[current_user].is_top,
        is_me,
      };
    }

    const { room_id, is_sort, updated_at } = data;
    const index = getIndexByRoomId(room_id);
    if (index < 0) {
      const newChatItem = {
        notice: data.notice,
        is_top: data.is_top,
        updated_at: data.updated_at,
        text: data.text,
        is_me: data.is_me,
        is_read: data.is_read,
        room_id: data.room_id,
        unanswer: data.is_unanswer,
        create_time: '剛剛',
        __msg_time: getMsgTime(data.updated_at),
      };

      assignServiceInfo(newChatItem, data);

      const pending = pendingServiceInfo.get(String(room_id));
      if (pending) {
        assignServiceInfo(newChatItem, pending);
        pendingServiceInfo.delete(String(room_id));
      }

      if (data.user) {
        newChatItem.nickname = data.user.nickname;
        newChatItem.avatar = data.user.avatar;
        newChatItem.level = data.user.level;
        newChatItem.level_image = data.user.level_image;
      }

      setMapItem(room_id, updated_at);
      insertChatItem(newChatItem);
      return;
    }

    // 如果消息列表裏的消息，比新收到的更新，則不需要更新
    const item = list.value[index];
    if (item.updated_at > data.updated_at) {
      return;
    }

    setMapItem(room_id, updated_at);

    let chatItem;
    if (is_sort) {
      chatItem = list.value.splice(index, 1)[0];
    } else {
      chatItem = list.value[index];
    }

    chatItem.notice = data.notice;
    chatItem.is_top = data.is_top;
    chatItem.updated_at = data.updated_at;
    chatItem.text = data.text;
    chatItem.is_me = data.is_me;
    chatItem.is_read = data.is_read;
    chatItem.unanswer = data.is_unanswer;
    chatItem.__msg_time = getMsgTime(data.updated_at);
    assignServiceInfo(chatItem, data);

    if (data.user) {
      chatItem.nickname = data.user.nickname;
      chatItem.avatar = data.user.avatar;
      chatItem.level = data.user.level;
    }
    // chatItem.level_image = data.user.level_image;

    if (is_sort) {
      chatItem.create_time = '剛剛';
      insertChatItem(chatItem);
    }
  };

  const userStore = useUserStore();
  const readChatItem = (teacher_uid, user_id) => {
    const chatItem = list.value.find((item) => item.ask_uid === user_id);
    if (!chatItem) return;
    const notice = chatItem.notice;
    chatItem.notice = 0;
    const teacher = userStore.getTeacherByUid(teacher_uid);
    const teacherNotice = teacher.notice;
    userStore.updateTeacherNotice(teacher_uid, teacherNotice - notice);
  };

  const updateNicknameRemark = (user_id, nicknameRemark) => {
    const chatItem = list.value.find((item) => item.ask_uid === user_id);
    if (!chatItem) return;
    chatItem.nickname_remark = nicknameRemark;
  };

  /**
   * 更新對話的屏蔽狀態
   * @param room_id
   * @param status
   */
  const updateChatShield = (room_id, status) => {
    const item = list.value.find((item) => String(item.room_id) === String(room_id));
    if (!item) return;

    item.is_black = status;
  };

  return {
    list,
    loading,
    moreData,
    error,
    msgNum,
    queryParams,

    clearError,
    setQueryParams,
    getMoreData,
    getMsgNum,
    reset,
    resetMsgNum,
    refreshCurrentList,
    setToTopStatus,
    updateChatItem,
    updateServiceInfo,
    readChatItem,
    updateNicknameRemark,
    updateList,
    updateChatShield,

    getChatItemByRoomId,
  };
});

export default useChatListStore;
